import CircularProgress from "@material-ui/core/CircularProgress";
import DescriptionIcon from "@material-ui/icons/Description";
import MuiButton from "@material-ui/core/Button";
import React, { FC, useEffect, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import CampaignBuilderForm from "../../components/redesign/modules/CampaignBuilderForm";
import EmptyCampaignTemplatesPage from "../../components/CampaignCreation/EmptyCampaignTemplatesPage/EmptyCampaignTemplatesPage";
import api from "../../api";
import { CampaignTemplate } from "../../types/campaignTypes";
import { ReduxApplicationState } from "../../types/ReduxTypes/ReduxApplicationState";
import { initializeGlobalAdvertiser } from "../../redux/reducers/agency";
import { initializeStateWithTemplateData } from "../../utils/initializeStateWithTemplateData";
import { resetCampaignToCreateState } from "../../redux/reducers/campaignToCreate";
import { useGlobalSelectedAdvertiser } from "../../hooks/useGlobalSelectedAdvertiser";

const PickerContent = styled("div")({
  paddingBottom: 96,
});

const TemplateGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
  gap: 16,
  marginBottom: 32,
});

const TemplateCardBase = styled("div")(({ theme }: { theme: Theme }) => ({
  border: `2px solid ${theme.palette.brand.gray200}`,
  borderRadius: 12,
  padding: 20,
  cursor: "pointer",
  background: theme.palette.brand.white,
  transition: "border-color 0.15s, box-shadow 0.15s",
  "&:hover": {
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
}));

const TemplateCardSelected = styled(TemplateCardBase)(({ theme }: { theme: Theme }) => ({
  border: `2px solid ${theme.palette.brand.coral}`,
}));

const TemplateCardIcon = styled("div")(({ theme }: { theme: Theme }) => ({
  width: 40,
  height: 40,
  borderRadius: 8,
  background: theme.palette.brand.gray100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 12,
  color: theme.palette.brand.coral,
}));

const TemplateCardTitle = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 14,
  fontWeight: 700,
  color: theme.palette.brand.gray900,
  marginBottom: 4,
}));

const TemplateCardDescription = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray500,
  lineHeight: 1.4,
}));

const Footer = styled("div")(({ theme }: { theme: Theme }) => ({
  position: "fixed",
  bottom: 0,
  left: 200,
  right: 0,
  background: theme.palette.brand.white,
  borderTop: `1px solid ${theme.palette.brand.gray200}`,
  padding: "12px 24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 12,
  zIndex: 100,
}));

const GetStartedButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 20px",
  borderRadius: 8,
  textTransform: "none",
  boxShadow: "none",
  minWidth: 140,
  "&:hover": {
    background: theme.palette.brand.coralHover,
    transform: "translateY(-1px)",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    background: theme.palette.brand.coral,
    color: theme.palette.brand.white,
    opacity: 0.6,
  },
}));

const LoaderWrapper = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: 300,
});

const CampaignFromTemplateCreatePageRedesign: FC = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: ReduxApplicationState) => state.user.currentUser);
  const advertisers = useSelector((state: ReduxApplicationState) => state.agency.advertisers);
  const { selectedAdvertiserId } = useGlobalSelectedAdvertiser();

  const [view, setView] = useState<"picker" | "form">("picker");
  const [templates, setTemplates] = useState<CampaignTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<CampaignTemplate | null>(null);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (user && selectedAdvertiserId) {
      const fetchTemplates = async () => {
        setIsLoading(true);
        try {
          const data = await api.getCampaignTemplatesByUser(user);
          if (isMounted) {
            setTemplates(
              (data as CampaignTemplate[]).filter(
                (t) => !t.selectedAdvertiser || t.selectedAdvertiser === selectedAdvertiserId,
              ),
            );
          }
        } catch (error) {
          console.error("Failed to fetch templates:", error);
        } finally {
          if (isMounted) setIsLoading(false);
        }
      };

      fetchTemplates();
    }

    return () => {
      isMounted = false;
    };
  }, [user, selectedAdvertiserId]);

  const handleGetStarted = async () => {
    if (!selectedTemplate) return;
    setIsStarting(true);
    await initializeStateWithTemplateData(selectedTemplate, dispatch, advertisers);
    dispatch(initializeGlobalAdvertiser());
    setIsStarting(false);
    setView("form");
  };

  const handleBack = () => {
    dispatch(resetCampaignToCreateState());
    setSelectedTemplate(null);
    setView("picker");
  };

  if (view === "form" && selectedTemplate) {
    return (
      <CampaignBuilderForm
        mode="campaign"
        visibleOptionalPanels={selectedTemplate.templateSteps}
        skipInitialReset
        onBack={handleBack}
      />
    );
  }

  if (isLoading) {
    return (
      <LoaderWrapper>
        <CircularProgress style={{ color: "#F06449" }} />
      </LoaderWrapper>
    );
  }

  if (!isLoading && templates.length === 0) {
    return <EmptyCampaignTemplatesPage />;
  }

  return (
    <PickerContent>
      <TemplateGrid>
        {templates.map((template) => {
          const isSelected = selectedTemplate?.id === template.id;
          const CardComponent = isSelected ? TemplateCardSelected : TemplateCardBase;
          return (
            <CardComponent key={template.id} onClick={() => setSelectedTemplate(template)}>
              <TemplateCardIcon>
                <DescriptionIcon fontSize="small" />
              </TemplateCardIcon>
              <TemplateCardTitle>{template.templateName}</TemplateCardTitle>
              {template.name && <TemplateCardDescription>{template.name}</TemplateCardDescription>}
            </CardComponent>
          );
        })}
      </TemplateGrid>
      <Footer>
        <GetStartedButton onClick={handleGetStarted} disabled={!selectedTemplate || isStarting} disableElevation>
          {isStarting ? <CircularProgress size={16} style={{ color: "inherit" }} /> : "Get started"}
        </GetStartedButton>
      </Footer>
    </PickerContent>
  );
};

export default CampaignFromTemplateCreatePageRedesign;
